import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, LayoutGrid, List as ListIcon, Loader2, Trash2, Pencil, Send,
  Calendar as CalendarIcon, AlertTriangle, MessageCircle, History, ArrowRight,
  CheckSquare, Square, X, Archive, ClipboardCopy, Settings2, FileStack, Target,
  Users, Plus as PlusIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { todayISO } from "@/lib/format";


const ESTADO_META = {
  por_hacer: { label: "Por hacer", color: "bg-zinc-500/15 text-zinc-200 border-zinc-500/30", dot: "bg-zinc-400" },
  en_curso:  { label: "En curso",  color: "bg-blue-500/15 text-blue-200 border-blue-500/30", dot: "bg-blue-400" },
  revision:  { label: "Revisión",  color: "bg-amber-500/15 text-amber-200 border-amber-500/30", dot: "bg-amber-400" },
  hecho:     { label: "Hecho",     color: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", dot: "bg-emerald-400" },
  archivada: { label: "Archivada", color: "bg-zinc-700/20 text-zinc-400 border-zinc-700", dot: "bg-zinc-600" },
};
const KANBAN_ORDER = ["por_hacer", "en_curso", "revision", "hecho"];

const PRIO_META = {
  baja:    { label: "Baja",    color: "bg-zinc-700/30 text-zinc-300" },
  media:   { label: "Media",   color: "bg-blue-500/15 text-blue-300" },
  alta:    { label: "Alta",    color: "bg-amber-500/15 text-amber-300" },
  urgente: { label: "Urgente", color: "bg-red-500/20 text-red-300" },
};

const EMPTY = {
  titulo: "", descripcion: "",
  estado: "por_hacer", prioridad: "media",
  asignados: [], fecha_limite: "", tags: [],
  peticion_id: "", lead_id: "",
};

export default function Tareas() {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const [tareas, setTareas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [users, setUsers] = useState([]);
  const [plantillas, setPlantillas] = useState([]);

  const [view, setView] = useState("kanban");
  const [search, setSearch] = useState("");
  const [filtroAsig, setFiltroAsig] = useState("");
  const [filtroPrio, setFiltroPrio] = useState("");
  const [mias, setMias] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);

  const [openNew, setOpenNew] = useState(false);
  const [openPlantillas, setOpenPlantillas] = useState(false);
  const [openAplicar, setOpenAplicar] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (filtroAsig) params.asignado_id = filtroAsig;
      if (filtroPrio) params.prioridad = filtroPrio;
      if (mias) params.mias = true;
      const [t, r] = await Promise.all([
        api.get("/tareas", { params }),
        api.get("/tareas/resumen"),
      ]);
      setTareas(t.data || []);
      setResumen(r.data || null);
    } catch (err) {
      toast.error("Error cargando tareas");
    } finally {
      setLoading(false);
    }
  }, [search, filtroAsig, filtroPrio, mias]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {});
    api.get("/tarea-plantillas").then(({ data }) => setPlantillas(data || [])).catch(() => {});
  }, []);

  const kanbanCols = useMemo(() => {
    const cols = {};
    KANBAN_ORDER.forEach((e) => { cols[e] = []; });
    tareas.forEach((t) => { if (KANBAN_ORDER.includes(t.estado)) cols[t.estado].push(t); });
    return cols;
  }, [tareas]);

  const submitNew = async (e) => {
    e?.preventDefault();
    if (!form.titulo.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      const payload = {
        ...form,
        fecha_limite: form.fecha_limite || null,
        peticion_id: form.peticion_id || null,
        lead_id: form.lead_id || null,
      };
      const { data } = await api.post("/tareas", payload);
      toast.success(`Tarea «${data.titulo}» creada`);
      setOpenNew(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const moveTo = async (tarea, nuevoEstado) => {
    if (tarea.estado === nuevoEstado) return;
    setTareas((curr) => curr.map((t) => t.id === tarea.id ? { ...t, estado: nuevoEstado } : t));
    try {
      await api.patch(`/tareas/${tarea.id}`, { estado: nuevoEstado });
      load();
    } catch (err) {
      toast.error("Error moviendo");
      load();
    }
  };

  const onDragStart = (t) => (e) => { setDragging(t); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (estado) => (e) => {
    e.preventDefault();
    if (dragging) moveTo(dragging, estado);
    setDragging(null);
  };

  const remove = async (t) => {
    if (!isAdmin) return toast.error("Sólo admin");
    if (!window.confirm(`¿Eliminar tarea «${t.titulo}»?`)) return;
    try {
      await api.delete(`/tareas/${t.id}`);
      toast.success("Eliminada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-6" data-testid="tareas-page">
      <PageHeader
        title="Tareas del equipo"
        subtitle="Tablero Kanban interno · pendientes, plantillas y recordatorios"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setOpenPlantillas(true)}
              className="bg-transparent border-zinc-700 text-zinc-300" data-testid="tareas-plantillas-btn">
              <FileStack className="w-4 h-4 mr-2" /> Plantillas
            </Button>
            <div className="flex bg-zinc-900/60 border border-zinc-800 rounded-md p-0.5">
              <button onClick={() => setView("kanban")} data-testid="view-kanban"
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 ${view === "kanban" ? "bg-amber-500/20 text-amber-200" : "text-zinc-400"}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> Kanban
              </button>
              <button onClick={() => setView("list")} data-testid="view-list"
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 ${view === "list" ? "bg-amber-500/20 text-amber-200" : "text-zinc-400"}`}>
                <ListIcon className="w-3.5 h-3.5" /> Lista
              </button>
            </div>
            <Button onClick={() => { setForm(EMPTY); setOpenNew(true); }}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="tareas-new-btn">
              <Plus className="w-4 h-4 mr-2" /> Nueva tarea
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <KPI label="Total activas" value={resumen.total} hi testid="kpi-total" />
          <KPI label="Mías" value={resumen.mias_activas} accent="text-amber-300" testid="kpi-mias" />
          <KPI label="Mías vencidas" value={resumen.mias_vencidas}
            accent={resumen.mias_vencidas > 0 ? "text-red-300" : "text-zinc-300"} testid="kpi-mias-venc" />
          <KPI label="Mías hoy" value={resumen.mias_hoy} accent="text-amber-300" testid="kpi-mias-hoy" />
          <KPI label="Urgentes" value={resumen.por_prioridad?.urgente || 0}
            accent={(resumen.por_prioridad?.urgente || 0) > 0 ? "text-red-300" : "text-zinc-300"} testid="kpi-urg" />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar título, descripción, etiqueta…"
            className="bg-zinc-950 border-zinc-800 pl-8" data-testid="filter-search" />
        </div>
        <Select value={filtroAsig || "__ALL__"} onValueChange={(v) => setFiltroAsig(v === "__ALL__" ? "" : v)}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="filter-asignado">
            <SelectValue placeholder="Asignado" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="__ALL__">Todos</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroPrio || "__ALL__"} onValueChange={(v) => setFiltroPrio(v === "__ALL__" ? "" : v)}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 w-36" data-testid="filter-prio">
            <SelectValue placeholder="Prioridad" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="__ALL__">Todas</SelectItem>
            {Object.keys(PRIO_META).map((p) => <SelectItem key={p} value={p}>{PRIO_META[p].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input type="checkbox" checked={mias} onChange={(e) => setMias(e.target.checked)}
            className="accent-amber-500" data-testid="filter-mias" />
          Sólo mías
        </label>
        {(search || filtroAsig || filtroPrio || mias) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFiltroAsig(""); setFiltroPrio(""); setMias(false); }}
            className="text-zinc-400 hover:text-zinc-100"><X className="w-3.5 h-3.5 mr-1" /> Limpiar</Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : view === "kanban" ? (
        <div className="overflow-x-auto" data-testid="kanban-board">
          <div className="grid grid-flow-col auto-cols-[280px] gap-3 pb-4">
            {KANBAN_ORDER.map((estado) => {
              const m = ESTADO_META[estado];
              const items = kanbanCols[estado] || [];
              return (
                <div key={estado}
                  onDragOver={onDragOver} onDrop={onDrop(estado)}
                  className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg flex flex-col min-h-[400px]"
                  data-testid={`kanban-col-${estado}`}>
                  <div className={`px-3 py-2 rounded-t-lg border-b border-zinc-800 flex items-center justify-between ${m.color}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${m.dot}`}></span>
                      <span className="text-xs font-semibold uppercase tracking-wider">{m.label}</span>
                    </div>
                    <span className="text-[10px] text-zinc-400">{items.length}</span>
                  </div>
                  <div className="p-2 space-y-2 flex-1">
                    {items.map((t) => (
                      <TareaCard key={t.id} tarea={t}
                        onClick={() => setDetailId(t.id)}
                        onDragStart={onDragStart(t)} />
                    ))}
                    {items.length === 0 && (
                      <div className="text-center text-xs text-zinc-600 py-8 italic">— vacía —</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="tareas-list">
          {tareas.length === 0 ? (
            <div className="text-center text-zinc-500 py-12">No hay tareas.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Tarea</th>
                  <th className="text-left p-2.5">Estado</th>
                  <th className="text-left p-2.5">Prio</th>
                  <th className="text-left p-2.5">Asignados</th>
                  <th className="text-left p-2.5">Fecha límite</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {tareas.map((t) => {
                  const m = ESTADO_META[t.estado];
                  return (
                    <tr key={t.id} onClick={() => setDetailId(t.id)}
                      className="border-b border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer"
                      data-testid={`tarea-row-${t.id}`}>
                      <td className="p-2.5">
                        <div className="font-medium text-zinc-100">{t.titulo}</div>
                        {t.tags?.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {t.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="text-[9px] bg-zinc-800 text-zinc-400 px-1 rounded">{tag}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${m.color}`}>{m.label}</span></td>
                      <td className="p-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full ${PRIO_META[t.prioridad].color}`}>{PRIO_META[t.prioridad].label}</span></td>
                      <td className="p-2.5 text-xs text-zinc-300">
                        {(t.asignados_data || []).map((u) => u.name).join(", ") || "—"}
                      </td>
                      <td className="p-2.5 text-xs">
                        {t.fecha_limite ? (
                          <span className={t.vencida ? "text-red-300 font-medium" : t.vence_hoy ? "text-amber-300 font-medium" : "text-zinc-400"}>
                            {t.vencida && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                            {t.fecha_limite}
                          </span>
                        ) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="p-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {isAdmin && (
                          <Button size="icon" variant="ghost" onClick={() => remove(t)}
                            className="h-7 w-7 text-zinc-400 hover:text-red-400"
                            data-testid={`tarea-delete-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Dialog Nueva */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="tarea-new-dialog">
          <DialogHeader>
            <DialogTitle>Nueva tarea</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Asigna a uno o varios miembros del equipo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNew} className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Título *</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="form-titulo" required autoFocus />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                className="bg-zinc-900 border-zinc-800 min-h-[80px]" data-testid="form-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-prio"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {Object.keys(PRIO_META).map((p) => <SelectItem key={p} value={p}>{PRIO_META[p].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Fecha límite</Label>
                <Input type="date" value={form.fecha_limite}
                  onChange={(e) => setForm({ ...form, fecha_limite: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-fecha" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Asignados</Label>
              <UserMultiSelect users={users} value={form.asignados}
                onChange={(v) => setForm({ ...form, asignados: v })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Etiquetas (separadas por coma)</Label>
              <Input value={form.tags.join(", ")}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="compras, almacen, urgente"
                className="bg-zinc-900 border-zinc-800" data-testid="form-tags" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenNew(false)}
                className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              <Button type="submit" disabled={saving}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="form-save">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : "Crear tarea"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      {detailId && (
        <TareaDetail tareaId={detailId} users={users}
          onClose={() => setDetailId(null)}
          onChange={load} />
      )}

      {/* Plantillas */}
      {openPlantillas && (
        <PlantillasManager
          plantillas={plantillas}
          users={users}
          onClose={() => setOpenPlantillas(false)}
          onChange={() => api.get("/tarea-plantillas").then(({ data }) => setPlantillas(data || []))}
          onAplicar={(pl) => { setOpenPlantillas(false); setOpenAplicar(pl); }}
          isAdmin={isAdmin}
        />
      )}
      {openAplicar && (
        <AplicarPlantillaDialog
          plantilla={openAplicar}
          users={users}
          onClose={() => setOpenAplicar(null)}
          onDone={() => { setOpenAplicar(null); load(); }}
        />
      )}
    </div>
  );
}

function KPI({ label, value, accent = "text-zinc-100", hi = false, testid }) {
  return (
    <div className={`rounded-lg border ${hi ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"} px-4 py-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${hi ? "text-amber-300" : accent}`}>{value}</div>
    </div>
  );
}

function TareaCard({ tarea, onClick, onDragStart }) {
  const checklistDone = (tarea.checklist || []).filter((c) => c.hecho).length;
  const checklistTotal = (tarea.checklist || []).length;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`tarea-card-${tarea.id}`}
      className="bg-zinc-950 border border-zinc-800 hover:border-amber-500/50 rounded p-2.5 cursor-pointer transition"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${PRIO_META[tarea.prioridad].color}`}>
          {PRIO_META[tarea.prioridad].label}
        </span>
        {tarea.vencida && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
        {tarea.vence_hoy && !tarea.vencida && <CalendarIcon className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
      </div>
      <div className="font-medium text-sm text-zinc-100 mb-1.5">{tarea.titulo}</div>
      {tarea.tags?.length > 0 && (
        <div className="flex gap-1 flex-wrap mb-1.5">
          {tarea.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[9px] bg-zinc-800 text-zinc-400 px-1 rounded">{t}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-zinc-500">
        <span className="flex items-center gap-1">
          {tarea.fecha_limite && (
            <span className={tarea.vencida ? "text-red-400" : tarea.vence_hoy ? "text-amber-400" : ""}>
              <CalendarIcon className="w-2.5 h-2.5 inline mr-0.5" />
              {tarea.fecha_limite}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5">
          {checklistTotal > 0 && (
            <span className="text-zinc-400">{checklistDone}/{checklistTotal}</span>
          )}
          <span>{(tarea.asignados_data || []).slice(0, 3).map((u) => u.name?.[0]).join("")}</span>
        </span>
      </div>
    </div>
  );
}

function UserMultiSelect({ users, value, onChange }) {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-2 max-h-32 overflow-y-auto">
      <div className="flex flex-wrap gap-1.5">
        {users.map((u) => {
          const active = value.includes(u.id);
          return (
            <button key={u.id} type="button" onClick={() => toggle(u.id)}
              data-testid={`asignar-${u.id}`}
              className={`text-[10px] px-2 py-1 rounded border ${
                active ? "bg-amber-500/20 text-amber-200 border-amber-500/40" : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-100"
              }`}>
              {active ? "✓ " : ""}{u.name}
            </button>
          );
        })}
        {users.length === 0 && <span className="text-zinc-600 text-xs">No hay usuarios</span>}
      </div>
    </div>
  );
}

// ============== DETAIL ==============
function TareaDetail({ tareaId, users, onClose, onChange }) {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [t, setT] = useState(null);
  const [comentario, setComentario] = useState("");
  const [newCheck, setNewCheck] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tareas/${tareaId}`);
      setT(data);
      setEdit({
        titulo: data.titulo, descripcion: data.descripcion || "",
        prioridad: data.prioridad, fecha_limite: data.fecha_limite || "",
        asignados: data.asignados || [], tags: data.tags || [],
      });
    } catch (err) {
      toast.error("Error cargando");
      onClose();
    }
  }, [tareaId, onClose]);
  useEffect(() => { load(); }, [load]);

  if (!t) return null;
  const m = ESTADO_META[t.estado];

  const saveEdit = async () => {
    try {
      await api.patch(`/tareas/${tareaId}`, edit);
      toast.success("Guardado");
      setEditing(false);
      load(); onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };
  const addComment = async () => {
    if (!comentario.trim()) return;
    try {
      await api.post(`/tareas/${tareaId}/comentario`, { descripcion: comentario });
      setComentario(""); load();
    } catch (err) { toast.error("Error"); }
  };
  const addCheck = async () => {
    if (!newCheck.trim()) return;
    try {
      await api.post(`/tareas/${tareaId}/checklist`, { texto: newCheck });
      setNewCheck(""); load();
    } catch (err) { toast.error("Error"); }
  };
  const toggleCheck = async (item) => {
    await api.patch(`/tareas/${tareaId}/checklist/${item.id}`, { hecho: !item.hecho });
    load();
  };
  const removeCheck = async (item) => {
    await api.delete(`/tareas/${tareaId}/checklist/${item.id}`);
    load();
  };
  const changeEstado = async (e) => {
    await api.patch(`/tareas/${tareaId}`, { estado: e });
    load(); onChange();
  };
  const sendWA = async () => {
    try {
      const { data } = await api.post(`/tareas/${tareaId}/recordatorio-wa`);
      if (data.enviados.length > 0) toast.success(`📱 Enviado a: ${data.enviados.join(", ")}`);
      if (data.fallos.length > 0) toast.error(`Fallos: ${data.fallos.map((f) => f.name).join(", ")}`);
      if (data.enviados.length === 0 && data.fallos.length === 0)
        toast.warning("Ningún asignado tiene WhatsApp configurado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="tarea-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="min-w-0">
              <DialogTitle className="text-xl truncate">{t.titulo}</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Creada {(t.created_at || "").slice(0, 10)} por {t.created_by_nombre}
              </DialogDescription>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full border ${m.color}`}>{m.label}</span>
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 -mt-1 items-center">
          {[...KANBAN_ORDER, "archivada"].filter((e) => e !== t.estado).map((e) => (
            <button key={e} onClick={() => changeEstado(e)}
              data-testid={`btn-estado-${e}`}
              className={`text-[10px] px-2 py-1 rounded border ${ESTADO_META[e].color} hover:brightness-125`}>
              → {ESTADO_META[e].label}
            </button>
          ))}
          {t.asignados?.length > 0 && (
            <Button size="sm" onClick={sendWA}
              className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs ml-auto"
              data-testid="btn-wa-tarea">
              <MessageCircle className="w-3.5 h-3.5 mr-1" /> Recordar WA
            </Button>
          )}
        </div>

        {/* Datos */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-wider text-zinc-500">Datos</h3>
            {!editing && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}
                className="text-zinc-400 hover:text-amber-300" data-testid="tarea-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Editar
              </Button>
            )}
          </div>
          {!editing ? (
            <div className="space-y-2 text-sm">
              {t.descripcion && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Descripción</div>
                  <div className="text-zinc-200 whitespace-pre-wrap">{t.descripcion}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Prioridad</div>
                  <span className={`text-xs px-2 py-0.5 rounded ${PRIO_META[t.prioridad].color}`}>{PRIO_META[t.prioridad].label}</span>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Fecha límite</div>
                  <div className={t.vencida ? "text-red-300" : t.vence_hoy ? "text-amber-300" : "text-zinc-200"}>
                    {t.fecha_limite || <span className="italic text-zinc-600">—</span>}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Asignados</div>
                  <div className="text-zinc-200">{(t.asignados_data || []).map((u) => u.name).join(", ") || <span className="italic text-zinc-600">— sin asignar —</span>}</div>
                </div>
                {t.tags?.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Etiquetas</div>
                    <div className="flex gap-1 flex-wrap">
                      {t.tags.map((tag) => (
                        <span key={tag} className="text-[10px] bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Input value={edit.titulo} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
              <Textarea value={edit.descripcion} onChange={(e) => setEdit({ ...edit, descripcion: e.target.value })}
                className="bg-zinc-900 border-zinc-800 min-h-[60px]" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={edit.prioridad} onValueChange={(v) => setEdit({ ...edit, prioridad: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {Object.keys(PRIO_META).map((p) => <SelectItem key={p} value={p}>{PRIO_META[p].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={edit.fecha_limite}
                  onChange={(e) => setEdit({ ...edit, fecha_limite: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <UserMultiSelect users={users} value={edit.asignados}
                onChange={(v) => setEdit({ ...edit, asignados: v })} />
              <Input value={edit.tags.join(", ")}
                onChange={(e) => setEdit({ ...edit, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="etiquetas separadas por coma"
                className="bg-zinc-900 border-zinc-800" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(false)}
                  className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
                <Button onClick={saveEdit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                  data-testid="tarea-edit-save">Guardar</Button>
              </div>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
            <CheckSquare className="w-3.5 h-3.5" /> Checklist
            {t.checklist?.length > 0 && (
              <span className="text-zinc-400 text-[10px] normal-case">
                {t.checklist.filter((c) => c.hecho).length}/{t.checklist.length}
              </span>
            )}
          </h3>
          <div className="flex gap-2 mb-3">
            <Input value={newCheck} onChange={(e) => setNewCheck(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCheck()}
              placeholder="Añadir sub-tarea…"
              className="bg-zinc-900 border-zinc-800" data-testid="check-input" />
            <Button onClick={addCheck} disabled={!newCheck.trim()}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="check-add">
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="space-y-1.5">
            {(t.checklist || []).map((item) => (
              <div key={item.id} className="flex items-center gap-2 text-sm group" data-testid={`check-${item.id}`}>
                <button onClick={() => toggleCheck(item)}
                  className="text-zinc-400 hover:text-amber-300"
                  data-testid={`check-toggle-${item.id}`}>
                  {item.hecho ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4" />}
                </button>
                <span className={`flex-1 ${item.hecho ? "line-through text-zinc-500" : "text-zinc-200"}`}>
                  {item.texto}
                </span>
                <button onClick={() => removeCheck(item)}
                  className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100"
                  data-testid={`check-remove-${item.id}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {!t.checklist?.length && <div className="text-xs text-zinc-600 italic">— sin items —</div>}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2 mb-3">
            <History className="w-3.5 h-3.5" /> Historial
          </h3>
          <div className="flex gap-2 mb-3">
            <Input value={comentario} onChange={(e) => setComentario(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()}
              placeholder="Añade un comentario…"
              className="bg-zinc-900 border-zinc-800" data-testid="comment-input" />
            <Button onClick={addComment} disabled={!comentario.trim()}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="comment-send">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(t.timeline || []).slice().reverse().map((ev) => (
              <div key={ev.id} className="bg-zinc-950 border border-zinc-800/60 rounded p-2.5 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium text-zinc-300">{ev.autor_nombre}</span>
                  <span className="text-[10px] text-zinc-500">{(ev.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-zinc-200">{ev.descripcion}</div>
              </div>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={async () => {
              if (window.confirm(`¿Eliminar tarea «${t.titulo}»?`)) {
                await api.delete(`/tareas/${tareaId}`);
                onChange(); onClose();
              }
            }} className="text-red-400 hover:text-red-300" data-testid="tarea-detail-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar tarea
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============== PLANTILLAS MANAGER ==============
function PlantillasManager({ plantillas, users, onClose, onChange, onAplicar, isAdmin }) {
  const [editing, setEditing] = useState(null);  // plantilla en edición o "new"
  const [form, setForm] = useState({ nombre: "", descripcion: "", items: [] });

  const openNew = () => { setForm({ nombre: "", descripcion: "", items: [] }); setEditing("new"); };
  const openEdit = (pl) => { setForm({ nombre: pl.nombre, descripcion: pl.descripcion || "", items: pl.items || [] }); setEditing(pl); };

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Nombre obligatorio");
    if (!form.items.length) return toast.error("Añade al menos 1 ítem");
    try {
      if (editing === "new") {
        await api.post("/tarea-plantillas", form);
        toast.success("Plantilla creada");
      } else {
        await api.patch(`/tarea-plantillas/${editing.id}`, form);
        toast.success("Plantilla actualizada");
      }
      setEditing(null);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };
  const remove = async (pl) => {
    if (!window.confirm(`¿Eliminar plantilla «${pl.nombre}»?`)) return;
    try {
      await api.delete(`/tarea-plantillas/${pl.id}`);
      toast.success("Eliminada"); onChange();
    } catch (err) { toast.error("Error"); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { titulo: "", prioridad: "media", dias_offset: 0, checklist: [], tags: [] }] });
  const updateItem = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="plantillas-dialog">
        <DialogHeader>
          <DialogTitle>📋 Plantillas de tareas</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Reutiliza checklists para flujos repetitivos (onboarding cliente, preparación feria, etc.).
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre plantilla"
              className="bg-zinc-900 border-zinc-800" data-testid="pl-nombre" />
            <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Descripción"
              className="bg-zinc-900 border-zinc-800 min-h-[50px]" />
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-zinc-400">Ítems</Label>
                <Button size="sm" onClick={addItem}
                  className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs"
                  data-testid="pl-add-item">
                  <Plus className="w-3 h-3 mr-1" /> Añadir ítem
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded p-2.5 space-y-2"
                    data-testid={`pl-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={it.titulo} onChange={(e) => updateItem(i, { titulo: e.target.value })}
                        placeholder="Título"
                        className="bg-zinc-950 border-zinc-800 flex-1" />
                      <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={it.prioridad} onValueChange={(v) => updateItem(i, { prioridad: v })}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-xs h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                          {Object.keys(PRIO_META).map((p) => <SelectItem key={p} value={p}>{PRIO_META[p].label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div>
                        <Input type="number" value={it.dias_offset}
                          onChange={(e) => updateItem(i, { dias_offset: parseInt(e.target.value || 0, 10) })}
                          placeholder="Días desde inicio"
                          className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                      </div>
                      <Input value={(it.tags || []).join(", ")}
                        onChange={(e) => updateItem(i, { tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        placeholder="etiquetas"
                        className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                    </div>
                    <Input value={(it.checklist || []).join(" | ")}
                      onChange={(e) => updateItem(i, { checklist: e.target.value.split("|").map((s) => s.trim()).filter(Boolean) })}
                      placeholder="checklist (separadas por |)"
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                  </div>
                ))}
                {form.items.length === 0 && <div className="text-xs text-zinc-600 text-center py-4">Sin ítems aún</div>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}
                className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              <Button onClick={save} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                data-testid="pl-save">Guardar</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2">
            {isAdmin && (
              <Button onClick={openNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 w-full"
                data-testid="pl-new-btn">
                <Plus className="w-4 h-4 mr-2" /> Nueva plantilla
              </Button>
            )}
            {plantillas.map((pl) => (
              <div key={pl.id} className="bg-zinc-900/40 border border-zinc-800 rounded p-3 flex items-center gap-3"
                data-testid={`pl-row-${pl.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-100">{pl.nombre}</div>
                  <div className="text-xs text-zinc-400">{pl.items?.length || 0} ítems · {pl.descripcion}</div>
                </div>
                <Button size="sm" onClick={() => onAplicar(pl)}
                  className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs"
                  data-testid={`pl-aplicar-${pl.id}`}>
                  <ClipboardCopy className="w-3 h-3 mr-1" /> Aplicar
                </Button>
                {isAdmin && (
                  <>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(pl)}
                      className="h-7 w-7 text-zinc-400 hover:text-amber-300"
                      data-testid={`pl-edit-${pl.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(pl)}
                      className="h-7 w-7 text-zinc-400 hover:text-red-400"
                      data-testid={`pl-del-${pl.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}
            {plantillas.length === 0 && <div className="text-center text-zinc-500 py-8">Sin plantillas.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AplicarPlantillaDialog({ plantilla, users, onClose, onDone }) {
  const [fechaBase, setFechaBase] = useState(todayISO());
  const [asignados, setAsignados] = useState([]);
  const [loading, setLoading] = useState(false);

  const aplicar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tarea-plantillas/${plantilla.id}/aplicar`, {
        fecha_base: fechaBase, asignados,
      });
      toast.success(`${data.tareas_creadas} tareas creadas desde «${data.plantilla}»`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="pl-aplicar-dialog">
        <DialogHeader>
          <DialogTitle>Aplicar plantilla «{plantilla.nombre}»</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Se crearán {plantilla.items?.length || 0} tareas con fechas relativas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-400">Fecha base (día 0)</Label>
            <Input type="date" value={fechaBase} onChange={(e) => setFechaBase(e.target.value)}
              className="bg-zinc-900 border-zinc-800" data-testid="pl-aplicar-fecha" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Asignar a</Label>
            <UserMultiSelect users={users} value={asignados} onChange={setAsignados} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={aplicar} disabled={loading}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="pl-aplicar-confirm">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</> : "Crear tareas"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
